import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Search as SearchIcon, X, Copy, Pencil, Trash2, ArrowLeft, LayoutTemplate,
} from "lucide-react";

/**
 * Fase 15 — Template Modificatori (riutilizzabili).
 * Ogni template è una COPIA COMPLETA di uno o più gruppi.
 * L'applicazione a un prodotto è una copia PROFONDA e INDIPENDENTE:
 * successive modifiche al template non toccano i prodotti già configurati.
 */

const emptyOption = () => ({
  name: "", description: "", price_delta_cents: 0,
  sort_order: 0, active: true, default_selected: false, badges: [],
});

const emptyGroup = (idx = 0) => ({
  name: "", description: "", customer_description: "",
  active: true, required: false, multi: false,
  min_select: 0, max_select: 1, sort_order: idx,
  options: [emptyOption()],
});

const emptyTemplate = () => ({
  name: "", description: "", groups: [emptyGroup(0)],
});

function normalise(s) {
  return (s || "").toString().toLowerCase();
}

// ------------------------------------------------------------------
// Group editor (nested inside the template dialog)
// ------------------------------------------------------------------
function OptionEditor({ opt, onChange, onRemove, onDuplicate, index }) {
  const badgesText = (opt.badges || []).join(", ");
  return (
    <div className="rounded-lg border border-sand-300 p-2 bg-sand-50/40 grid grid-cols-12 gap-2 items-end"
         data-testid={`tpl-opt-${index}`}>
      <div className="col-span-4">
        <Label className="text-[10px]">Nome</Label>
        <Input value={opt.name}
               onChange={(e) => onChange({ ...opt, name: e.target.value })}
               data-testid={`tpl-opt-name-${index}`} />
      </div>
      <div className="col-span-3">
        <Label className="text-[10px]">Badge (separati da ,)</Label>
        <Input value={badgesText}
               placeholder="es. bio, consigliato"
               onChange={(e) => onChange({ ...opt,
                 badges: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
               data-testid={`tpl-opt-badges-${index}`} />
      </div>
      <div className="col-span-2">
        <Label className="text-[10px]">Δ prezzo (€)</Label>
        <Input type="number" step="0.10"
               value={((opt.price_delta_cents || 0) / 100).toFixed(2)}
               onChange={(e) => onChange({ ...opt,
                 price_delta_cents: Math.round(parseFloat(e.target.value || 0) * 100) })}
               data-testid={`tpl-opt-price-${index}`} />
      </div>
      <div className="col-span-1 text-center">
        <Label className="text-[10px]">Attiva</Label>
        <div className="pt-1.5">
          <Switch checked={opt.active !== false}
                  onCheckedChange={(v) => onChange({ ...opt, active: v })}
                  data-testid={`tpl-opt-active-${index}`} />
        </div>
      </div>
      <div className="col-span-2 flex gap-1 justify-end">
        <button type="button" onClick={onDuplicate}
                className="p-1 hover:bg-sand-200 rounded" title="Duplica opzione"
                data-testid={`tpl-opt-dup-${index}`}>
          <Copy className="w-3.5 h-3.5" />
        </button>
        <button type="button" onClick={onRemove}
                className="p-1 hover:bg-red-50 text-red-700 rounded" title="Rimuovi"
                data-testid={`tpl-opt-del-${index}`}>
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function GroupEditor({ group, onChange, onRemove, onDuplicate, index }) {
  const setOpt = (oi, next) => onChange({
    ...group,
    options: group.options.map((o, i) => (i === oi ? next : o)),
  });
  const addOpt = () => onChange({ ...group,
    options: [...(group.options || []), { ...emptyOption(),
      sort_order: (group.options || []).length }] });
  const rmOpt = (oi) => onChange({ ...group,
    options: group.options.filter((_, i) => i !== oi) });
  const dupOpt = (oi) => onChange({ ...group,
    options: [...group.options.slice(0, oi + 1),
              { ...group.options[oi], name: `${group.options[oi].name} (Copia)` },
              ...group.options.slice(oi + 1)] });
  return (
    <div className="rounded-xl border border-sand-300 p-3 bg-white" data-testid={`tpl-group-${index}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-xs font-semibold text-ink-700">Gruppo #{index + 1}</div>
        <div className="flex gap-1">
          <button type="button" onClick={onDuplicate}
                  className="p-1 hover:bg-sand-200 rounded" title="Duplica gruppo"
                  data-testid={`tpl-group-dup-${index}`}>
            <Copy className="w-3.5 h-3.5" />
          </button>
          <button type="button" onClick={onRemove}
                  className="p-1 hover:bg-red-50 text-red-700 rounded" title="Rimuovi gruppo"
                  data-testid={`tpl-group-del-${index}`}>
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Nome gruppo</Label>
          <Input value={group.name}
                 onChange={(e) => onChange({ ...group, name: e.target.value })}
                 data-testid={`tpl-group-name-${index}`} />
        </div>
        <div>
          <Label className="text-xs">Descrizione cliente</Label>
          <Input value={group.customer_description || ""}
                 onChange={(e) => onChange({ ...group, customer_description: e.target.value })}
                 data-testid={`tpl-group-cust-${index}`} />
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2 mt-2">
        <label className="flex items-center gap-1 text-xs">
          <Switch checked={!!group.required}
                  onCheckedChange={(v) => onChange({ ...group, required: v })}
                  data-testid={`tpl-group-required-${index}`} />
          Obbligatorio
        </label>
        <label className="flex items-center gap-1 text-xs">
          <Switch checked={!!group.multi}
                  onCheckedChange={(v) => onChange({ ...group, multi: v,
                    min_select: v ? group.min_select : 0,
                    max_select: v ? Math.max(1, group.max_select) : 1 })}
                  data-testid={`tpl-group-multi-${index}`} />
          Multipla
        </label>
        {group.multi && (
          <>
            <div>
              <Label className="text-[10px]">Min</Label>
              <Input type="number" min="0" value={group.min_select}
                     onChange={(e) => onChange({ ...group, min_select: parseInt(e.target.value) || 0 })}
                     data-testid={`tpl-group-min-${index}`} />
            </div>
            <div>
              <Label className="text-[10px]">Max</Label>
              <Input type="number" min="1" value={group.max_select}
                     onChange={(e) => onChange({ ...group, max_select: parseInt(e.target.value) || 1 })}
                     data-testid={`tpl-group-max-${index}`} />
            </div>
          </>
        )}
      </div>
      <div className="mt-3">
        <div className="flex items-center justify-between">
          <Label className="text-xs">Opzioni ({group.options.length})</Label>
          <Button type="button" size="sm" variant="outline" onClick={addOpt}
                  data-testid={`tpl-group-add-opt-${index}`}>
            <Plus className="w-3 h-3 mr-1" /> Opzione
          </Button>
        </div>
        <div className="mt-2 space-y-1.5">
          {group.options.map((o, oi) => (
            <OptionEditor key={oi} index={oi} opt={o}
                          onChange={(next) => setOpt(oi, next)}
                          onRemove={() => rmOpt(oi)}
                          onDuplicate={() => dupOpt(oi)} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// Template create/edit dialog
// ------------------------------------------------------------------
function TemplateDialog({ template, onSaved, trigger }) {
  const isEdit = !!template;
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(emptyTemplate());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setF({
        name: template.name || "",
        description: template.description || "",
        groups: (template.groups || []).map((g, gi) => ({
          ...emptyGroup(gi), ...g,
          options: (g.options || []).map((o) => ({ ...emptyOption(), ...o,
            badges: Array.isArray(o.badges) ? o.badges : [] })),
        })),
      });
    } else {
      setF(emptyTemplate());
    }
  }, [open, isEdit, template]);

  const setGroup = (i, next) => setF({ ...f, groups: f.groups.map((g, idx) => idx === i ? next : g) });
  const addGroup = () => setF({ ...f, groups: [...f.groups, emptyGroup(f.groups.length)] });
  const rmGroup = (i) => setF({ ...f, groups: f.groups.filter((_, idx) => idx !== i) });
  const dupGroup = (i) => {
    const g = f.groups[i];
    setF({ ...f, groups: [
      ...f.groups.slice(0, i + 1),
      { ...g, name: `${g.name} (Copia)`,
        options: g.options.map((o) => ({ ...o })) },
      ...f.groups.slice(i + 1),
    ]});
  };

  const save = async () => {
    if (!f.name.trim()) return toast.error("Nome template obbligatorio");
    if (f.groups.length === 0) return toast.error("Aggiungi almeno un gruppo");
    for (const g of f.groups) {
      if (!g.name.trim()) return toast.error("Ogni gruppo deve avere un nome");
      if (g.options.length === 0) return toast.error(`Il gruppo "${g.name}" ha zero opzioni`);
      if (g.options.some((o) => !o.name.trim())) return toast.error("Ogni opzione deve avere un nome");
    }
    setBusy(true);
    try {
      const payload = {
        name: f.name.trim(),
        description: f.description || null,
        groups: f.groups.map((g, gi) => ({
          ...g, sort_order: gi,
          options: g.options.map((o, oi) => ({ ...o,
            sort_order: oi,
            price_delta_cents: parseInt(o.price_delta_cents) || 0,
            badges: Array.isArray(o.badges) ? o.badges.filter(Boolean) : [] })),
        })),
      };
      if (isEdit) await api.patch(`/admin/modifier-templates/${template.id}`, payload);
      else await api.post("/admin/modifier-templates", payload);
      setOpen(false); onSaved();
      toast.success(isEdit ? "Template aggiornato" : "Template creato");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button className="rounded-full" data-testid="tpl-new-btn">
            <Plus className="w-4 h-4 mr-1" /> Nuovo template
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Modifica "${template.name}"` : "Nuovo Template Modificatori"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Nome template</Label>
              <Input value={f.name} placeholder="es. Pizza, Hamburger, Cocktail"
                     onChange={(e) => setF({ ...f, name: e.target.value })}
                     data-testid="tpl-name" />
            </div>
            <div>
              <Label>Descrizione (facoltativa)</Label>
              <Input value={f.description || ""}
                     onChange={(e) => setF({ ...f, description: e.target.value })}
                     data-testid="tpl-desc" />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Label>Gruppi ({f.groups.length})</Label>
            <Button type="button" size="sm" variant="outline" onClick={addGroup} data-testid="tpl-add-group">
              <Plus className="w-3 h-3 mr-1" /> Gruppo
            </Button>
          </div>

          <div className="space-y-2">
            {f.groups.map((g, gi) => (
              <GroupEditor key={gi} index={gi} group={g}
                           onChange={(next) => setGroup(gi, next)}
                           onRemove={() => rmGroup(gi)}
                           onDuplicate={() => dupGroup(gi)} />
            ))}
            {f.groups.length === 0 && (
              <div className="text-xs text-ink-500 italic p-4 text-center border border-dashed border-sand-300 rounded-xl">
                Nessun gruppo. Aggiungine uno per iniziare.
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
          <Button onClick={save} disabled={busy} data-testid="tpl-save">
            {busy ? "Salvo…" : (isEdit ? "Salva modifiche" : "Crea template")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------
// Main page
// ------------------------------------------------------------------
export default function AdminModifierTemplates() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/modifier-templates");
      setItems(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const nq = normalise(q).trim();
    const src = nq
      ? items.filter((t) => normalise(t.name).includes(nq)
                          || normalise(t.description).includes(nq))
      : items;
    return [...src].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [items, q]);

  const doDuplicate = async (id) => {
    try {
      await api.post(`/admin/modifier-templates/${id}/duplicate`);
      toast.success("Template duplicato");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doDelete = async (t) => {
    if (!window.confirm(`Eliminare il template "${t.name}"?\nI prodotti a cui è già stato applicato non verranno modificati.`)) return;
    try {
      await api.delete(`/admin/modifier-templates/${t.id}`);
      toast.success("Template eliminato");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="tpl-page">
      <div className="flex items-center gap-2 flex-wrap">
        <Link to="/admin/menu" className="btn-outline text-xs py-1 px-3" data-testid="tpl-back">
          <ArrowLeft className="w-3 h-3 mr-1" /> Menu
        </Link>
        <div>
          <h1 className="font-serif text-2xl flex items-center gap-2">
            <LayoutTemplate className="w-6 h-6" /> Template Modificatori
          </h1>
          <p className="text-xs text-ink-500">
            Bundle riutilizzabili di gruppi modificatori. L'applicazione crea una copia
            indipendente sul prodotto.
          </p>
        </div>
        <div className="ml-auto"><TemplateDialog onSaved={load} /></div>
      </div>

      <div className="relative max-w-md">
        <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Cerca template…" className="pl-9 pr-8"
               data-testid="tpl-search" />
        {q && (
          <button onClick={() => setQ("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-600"
                  data-testid="tpl-search-clear" aria-label="Pulisci">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-ink-500 p-6" data-testid="tpl-loading">Caricamento…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-sand-300 p-10 text-center text-sm text-ink-500"
             data-testid="tpl-empty">
          {q ? "Nessun template corrisponde alla ricerca." :
               "Nessun template ancora. Creane uno per riutilizzare rapidamente i gruppi modificatori."}
        </div>
      ) : (
        <div className="grid gap-2">
          {filtered.map((t) => (
            <div key={t.id} data-testid={`tpl-row-${t.id}`}
                 className="rounded-xl border border-sand-300 p-3 bg-white flex items-center gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="font-medium">{t.name}</div>
                {t.description && (
                  <div className="text-xs text-ink-500 line-clamp-1">{t.description}</div>
                )}
                <div className="text-xs text-ink-500 mt-1">
                  {t.group_count} grupp{t.group_count === 1 ? "o" : "i"} ·
                  {" "}{t.option_count} opzion{t.option_count === 1 ? "e" : "i"}
                  {t.updated_at && <> · aggiornato {String(t.updated_at).slice(0, 16).replace("T", " ")}</>}
                </div>
                <div className="mt-1 text-[11px] text-ink-500">
                  {(t.groups || []).map((g) => g.name).filter(Boolean).join(" · ")}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <TemplateDialog template={t} onSaved={load} trigger={
                  <button className="p-1.5 rounded-full hover:bg-sand-200"
                          data-testid={`tpl-edit-${t.id}`} title="Modifica">
                    <Pencil className="w-4 h-4" />
                  </button>
                } />
                <button onClick={() => doDuplicate(t.id)}
                        className="p-1.5 rounded-full hover:bg-sand-200"
                        data-testid={`tpl-dup-${t.id}`} title="Duplica">
                  <Copy className="w-4 h-4" />
                </button>
                <button onClick={() => doDelete(t)}
                        className="p-1.5 rounded-full text-red-700 hover:bg-red-50"
                        data-testid={`tpl-del-${t.id}`} title="Elimina">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------
// Reusable "Applica Template" picker (used inside ProductDialog)
// ------------------------------------------------------------------
export function ApplyTemplatePicker({ productId, onApplied, disabled }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/modifier-templates");
      setItems(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const filtered = useMemo(() => {
    const nq = normalise(q).trim();
    return [...items]
      .filter((t) => !nq || normalise(t.name).includes(nq))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [items, q]);

  const apply = async (tid) => {
    if (!productId) return toast.error("Salva prima il prodotto per applicare un template");
    setBusy(true);
    try {
      const r = await api.post(`/admin/modifier-templates/${tid}/apply/${productId}`);
      toast.success(`Template applicato · +${r.data.added_group_ids?.length || 0} gruppi`);
      setOpen(false);
      onApplied?.(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" size="sm" variant="outline" disabled={disabled}
                data-testid="apply-template-btn">
          <LayoutTemplate className="w-3.5 h-3.5 mr-1" /> Applica Template
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Applica un template a questo prodotto</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-ink-500">
          I gruppi del template vengono COPIATI (copia indipendente): eventuali
          modifiche al template <b>non</b> toccheranno questo prodotto in seguito.
          Puoi applicare più template allo stesso prodotto.
        </p>
        <div className="relative mt-2">
          <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Cerca template…" className="pl-9"
                 data-testid="apply-tpl-search" />
        </div>
        <div className="mt-3 max-h-80 overflow-y-auto space-y-1.5">
          {filtered.length === 0 ? (
            <div className="text-xs text-ink-500 italic p-4 text-center border border-dashed border-sand-300 rounded-xl">
              {items.length === 0
                ? "Nessun template ancora. Creane uno da Menu → Template Modificatori."
                : "Nessun risultato per la ricerca."}
            </div>
          ) : filtered.map((t) => (
            <button key={t.id} onClick={() => apply(t.id)} disabled={busy}
                    data-testid={`apply-tpl-${t.id}`}
                    className="w-full text-left rounded-lg border border-sand-300 p-3 hover:bg-sand-50 disabled:opacity-50">
              <div className="font-medium">{t.name}</div>
              <div className="text-xs text-ink-500">
                {t.group_count} grupp{t.group_count === 1 ? "o" : "i"} ·
                {" "}{t.option_count} opzion{t.option_count === 1 ? "e" : "i"}
              </div>
              <div className="text-[11px] text-ink-500 mt-0.5">
                {(t.groups || []).map((g) => g.name).filter(Boolean).join(" · ")}
              </div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------
// "Salva come Template" button (from an existing product)
// ------------------------------------------------------------------
export function SaveAsTemplateButton({ productId, productName, disabled, onSaved }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!productId) return toast.error("Salva prima il prodotto");
    const name = window.prompt("Nome del nuovo template:",
                               productName ? `${productName} · Template` : "");
    if (!name || !name.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/modifier-templates/from-product/${productId}`,
                     { name: name.trim() });
      toast.success("Template creato dai modificatori del prodotto");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Button type="button" size="sm" variant="outline" onClick={run}
            disabled={disabled || busy} data-testid="save-as-template-btn">
      <LayoutTemplate className="w-3.5 h-3.5 mr-1" />
      {busy ? "Salvo…" : "Salva come Template"}
    </Button>
  );
}
